import { EventTimeline, KnownMembership, MatrixClient } from 'matrix-js-sdk/src/matrix';
import { useEffect } from 'react';

import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import { getCallSessionDetails } from 'modules/RoomsListItem/utils';
import logger from 'utils/logging/faro';
import {
  CustomMeedioMatrixEvents,
  EMPTY_CALL_WATCHDOG_CHECK_INTERVAL_MS,
  EMPTY_CALL_WATCHDOG_DELAY_MS,
  markCallRoomEnded,
} from 'utils/matrixUtils';

// BUG-011: 5-minute window for the recent-MEETING_ENDED guard. Generous so
// we never double-emit even when the watchdog check races the in-call cleanup.
const WATCHDOG_ENDED_GUARD_MS = 5 * 60 * 1000;

/** Returns the current `call_room_id` from MESSENGER_CALL state, or null. */
const getActiveCallRoomId = (matrixClient: MatrixClient, messengerRoomId: string): string | null => {
  const state = matrixClient
    .getRoom(messengerRoomId)
    ?.getLiveTimeline()
    .getState(EventTimeline.FORWARDS)
    ?.getStateEvents(CustomMeedioMatrixEvents.MESSENGER_CALL, '');
  const callRoomId = state?.getContent()?.call_room_id as string | undefined;
  return callRoomId ?? null;
};

/**
 * Returns how many humans are in the call per `m.call.member` room state.
 * Returns `null` when this client has not joined the call room — we must not
 * treat "cannot see state" as "empty call" (BUG-002 / #8184).
 */
const getWatchdogParticipantCount = (matrixClient: MatrixClient, callRoomId: string): number | null => {
  const callRoom = matrixClient.getRoom(callRoomId);
  if (!callRoom || callRoom.getMyMembership() !== KnownMembership.Join) return null;
  return getCallSessionDetails(matrixClient, callRoom).participantsCount;
};

/**
 * #8184: true when the local user is the instant-meeting creator (`started_by`)
 * and still has an active RTC membership in the call room. Guards against the
 * reconnect race where the creator briefly drops to 0 RTC members (filling the
 * watchdog timer) and reconnects right as cleanup fires — we must not end the
 * meeting out from under the still-connected creator.
 */
const isLocalCreatorRtcConnected = (matrixClient: MatrixClient, callRoomId: string): boolean => {
  const callRoom = matrixClient.getRoom(callRoomId);
  if (!callRoom) return false;
  const callState = callRoom
    .getLiveTimeline()
    .getState(EventTimeline.FORWARDS)
    ?.getStateEvents(CustomMeedioMatrixEvents.MESSENGER_CALL_STATE, '')
    ?.getContent();
  const startedBy = callState?.started_by as string | undefined;
  const myUserId = matrixClient.getUserId();
  if (!startedBy || !myUserId || startedBy !== myUserId) return false;
  const session = matrixClient.matrixRTC.getActiveRoomSession(callRoom);
  return (session?.memberships ?? []).some((m) => m.sender === myUserId);
};

/** True when a MEETING_ENDED event for this call room exists within `windowMs`. */
const hasRecentMeetingEndedForRoom = (
  matrixClient: MatrixClient,
  messengerRoomId: string,
  callRoomId: string,
  windowMs: number
): boolean => {
  const events = matrixClient.getRoom(messengerRoomId)?.getLiveTimeline()?.getEvents();
  if (!events) return false;
  const floor = Date.now() - windowMs;
  for (const event of events) {
    if (event.getType() !== CustomMeedioMatrixEvents.MEETING_ENDED) continue;
    if (event.getContent()?.meeting_room_id !== callRoomId) continue;
    if (event.getTs() >= floor) return true;
  }
  return false;
};

const fireWatchdogCleanup = async (
  matrixClient: MatrixClient,
  messengerRoomId: string,
  callRoomId: string
): Promise<void> => {
  // Idempotency guard: re-read live state before acting.
  const liveCallRoomId = getActiveCallRoomId(matrixClient, messengerRoomId);
  if (liveCallRoomId !== callRoomId) {
    // Already cleared (hangup hook or another client got there first).
    return;
  }

  const stateParticipantCount = getWatchdogParticipantCount(matrixClient, callRoomId);
  if (stateParticipantCount === null || stateParticipantCount > 0) {
    if (stateParticipantCount !== null && stateParticipantCount > 0) {
      logger.info('useEmptyCallWatchdog: call room state shows participants — skipping cleanup', {
        callRoomId,
        stateParticipantCount,
      });
    }
    return;
  }

  // #8184: creator reconnected during the watchdog delay — do not end the meeting
  // while its creator is back and RTC-connected. Skip the whole cleanup so no
  // MEETING_ENDED row, state clear, or ended stamp is emitted.
  if (isLocalCreatorRtcConnected(matrixClient, callRoomId)) {
    logger.info('useEmptyCallWatchdog: creator RTC connected — skipping cleanup', { callRoomId });
    return;
  }

  // Guard: don't emit if MEETING_ENDED already exists recently.
  if (hasRecentMeetingEndedForRoom(matrixClient, messengerRoomId, callRoomId, WATCHDOG_ENDED_GUARD_MS)) {
    // Emit was already fired; still clear state if it's stale.
    try {
      await matrixClient.sendStateEvent(messengerRoomId, CustomMeedioMatrixEvents.MESSENGER_CALL, {}, '');
    } catch (err) {
      logger.warn('useEmptyCallWatchdog: failed to clear stale MESSENGER_CALL state', { err });
    }
    return;
  }

  // Compute duration from MESSENGER_CALL.started_at.
  const callState = matrixClient
    .getRoom(messengerRoomId)
    ?.getLiveTimeline()
    .getState(EventTimeline.FORWARDS)
    ?.getStateEvents(CustomMeedioMatrixEvents.MESSENGER_CALL, '');
  const startedAt = callState?.getContent()?.started_at as string | undefined;
  // BUG-015: include meeting_id so the renderer (BUG-003 detection) can distinguish
  // scheduled meetings (meeting_id present) from instant calls (meeting_id null/absent).
  const meetingId = callState?.getContent()?.meeting_id as string | undefined;
  const now = Date.now();
  const duration = startedAt ? Math.floor((now - Date.parse(startedAt)) / 1000) : 0;
  const meetingName = matrixClient.getRoom(callRoomId)?.name ?? '';
  const dedupeKey = `${messengerRoomId}|${callRoomId}|meeting_ended`;

  try {
    await matrixClient.sendEvent(messengerRoomId, CustomMeedioMatrixEvents.MEETING_ENDED, {
      meeting_room_id: callRoomId,
      ended_at: new Date(now).toISOString(),
      // BUG-007: auto-cleanup is not a host action — omit `ended_by`.
      duration,
      meeting_name: meetingName,
      meeting_id: meetingId ?? null,
      dedupe_key: dedupeKey,
      source: 'watchdog',
    });
  } catch (err) {
    logger.error('useEmptyCallWatchdog: failed to emit MEETING_ENDED', { err, callRoomId });
  }

  try {
    await matrixClient.sendStateEvent(messengerRoomId, CustomMeedioMatrixEvents.MESSENGER_CALL, {}, '');
  } catch (err) {
    logger.warn('useEmptyCallWatchdog: failed to clear MESSENGER_CALL state', { err });
  }

  // Stamp MESSENGER_CALL_STATE.ended on the call room so useActiveMeeting's
  // state-event check observes the cleanup without waiting for the MESSENGER_CALL
  // clear to propagate. Automatic cleanup is not a host action — omit ended_by.
  await markCallRoomEnded(matrixClient, callRoomId, messengerRoomId, new Date(now).toISOString(), 'watchdog');
};

/**
 * BUG-011: Chat-side watchdog that auto-clears a stale MESSENGER_CALL when
 * the call room has had 0 RTC participants for EMPTY_CALL_WATCHDOG_DELAY_MS.
 *
 * Runs inside ScheduledMeetingsWatcher (one instance per messenger room),
 * so it survives after the last call-room tab closes and the in-call hooks
 * (useMeetingLifecycleDelayedEvents, useMessengerCallHangup) unmount.
 */
const useEmptyCallWatchdog = (messengerRoomId: string | null | undefined): void => {
  const { matrixClient } = useMatrixContext();

  useEffect(() => {
    if (!messengerRoomId || !matrixClient) return;

    let emptyCallSince: number | null = null;
    let lastTrackedCallRoomId: string | null = null;

    const check = () => {
      const callRoomId = getActiveCallRoomId(matrixClient, messengerRoomId);

      if (!callRoomId) {
        emptyCallSince = null;
        lastTrackedCallRoomId = null;
        return;
      }

      // If the active call changed (e.g. a new call started), reset the timer.
      if (callRoomId !== lastTrackedCallRoomId) {
        emptyCallSince = null;
        lastTrackedCallRoomId = callRoomId;
      }

      // BUG-014: skip watchdog for scheduled meetings — they have dedicated
      // lifecycle cleanup (fireScheduledEnd, useMessengerCallHangup, delayed events).
      // Firing here would kill a scheduled meeting 30 s after the organiser
      // announces it, before any participant has had a chance to click "Join now".
      const messengerCallState = matrixClient
        .getRoom(messengerRoomId)
        ?.getLiveTimeline()
        .getState(EventTimeline.FORWARDS)
        ?.getStateEvents(CustomMeedioMatrixEvents.MESSENGER_CALL, '');
      const scheduledMeetingId = messengerCallState?.getContent()?.meeting_id as string | undefined;
      if (scheduledMeetingId) return;

      const participantCount = getWatchdogParticipantCount(matrixClient, callRoomId);

      if (participantCount === null || participantCount > 0) {
        emptyCallSince = null;
        return;
      }

      const now = Date.now();

      if (emptyCallSince === null) {
        emptyCallSince = now;
        return;
      }

      if (now - emptyCallSince >= EMPTY_CALL_WATCHDOG_DELAY_MS) {
        emptyCallSince = null; // Reset before async work to prevent re-entry.
        fireWatchdogCleanup(matrixClient, messengerRoomId, callRoomId).catch((err) => {
          logger.error('useEmptyCallWatchdog: cleanup error', { err });
        });
      }
    };

    const intervalId = window.setInterval(check, EMPTY_CALL_WATCHDOG_CHECK_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [matrixClient, messengerRoomId]);
};

export default useEmptyCallWatchdog;
